import { COMMISSION_FEES_PCT, LOCALE_ISO, TAXES_MULT, VEHICLES_FEES } from "../../thor.const";
import { Enums } from "../../thor.enums";

class LocaleNumber {
    public static $inject: string[] = ["$translate", "utilsService"];

    private octoberFirst2023: Date = new Date("October 01, 2023");

    constructor(private $translate: ng.translate.ITranslateService, private utils) {}

    public formatNumber(nbr: number): string {
        if (isNaN(nbr)) {
            return null;
        }
        return new Intl.NumberFormat(this.getLocaleIso(), this.numberFormatOptions(nbr)).format(nbr);
    }

    public formatCurrency(nbr: string | number, currency: Enums.Currency = Enums.Currency.EUR): string {
        if (typeof nbr === "number") {
            nbr = String(nbr);
        }
        if (!currency && nbr && nbr.includes(".")) {
            return nbr;
        }
        const amount = Number(nbr);

        if (isNaN(amount)) {
            return nbr;
        }
        return new Intl.NumberFormat(this.getLocaleIso(), this.numberFormatOptions(amount, currency)).format(amount);
    }

    public formatPercent(percent: number = 0): string {
        return new Intl.NumberFormat(this.getLocaleIso(), {
            style: "percent",
            maximumFractionDigits: 2
        }).format(percent / 100);
    }

    public getCurrencySymbol(currency: Enums.Currency) {
        if (!currency) {
            return null;
        }
        return new Intl.NumberFormat(this.getLocaleIso(), this.numberFormatOptions(1, currency))
            .format(1)
            .replace(/\d/, "")
            .trim();
    }

    public getJudicialFees({ format = true, start_at }: { format?: boolean; start_at?: string } = {}): string | number {
        const value =
            start_at && new Date(start_at) < this.octoberFirst2023
                ? COMMISSION_FEES_PCT.judicial_before_oct_2023
                : COMMISSION_FEES_PCT.judicial;
        return format ? this.formatPercent(value) : value;
    }

    public getVoluntaryFees({ format = true }: { format?: boolean } = {}): string | number {
        const value = COMMISSION_FEES_PCT.voluntary;
        return format ? this.formatPercent(value) : value;
    }

    public getVehiclesFees({
        currency = Enums.Currency.EUR,
        format = true,
        start_at,
        with_taxes = false
    }: {
        currency: Enums.Currency;
        format: boolean;
        start_at?: string;
        with_taxes: boolean;
    }): string | number {
        let value = (
            start_at && new Date(start_at) < this.octoberFirst2023
                ? VEHICLES_FEES.before_oct_2023
                : VEHICLES_FEES.current
        )[currency];
        value = with_taxes ? value * TAXES_MULT : value;
        return format ? this.formatCurrency(value, currency) : value;
    }

    /*
     * reduce price to 2 digits max after point
     * ex: 10.156789 => 10.16
     */
    public roundNbr(nbr: number, dec: number = 2): number {
        return Math.round((nbr + Number.EPSILON) * Math.pow(10, dec)) / Math.pow(10, dec);
    }

    public sumNbrs(nbrs: number[]): number {
        return nbrs.reduce((acc, nbr) => (nbr ? acc + nbr : acc), 0);
    }

    private getLocaleIso() {
        const locale = this.utils.getCurrentLang();
        const localeIso = LOCALE_ISO[locale].iso;

        if (locale !== Enums.Locale.En) {
            return localeIso;
        }

        const resolvedLocale = this.$translate.resolveClientLocale().replace(/_/, "-");

        return resolvedLocale && resolvedLocale !== localeIso ? resolvedLocale : localeIso;
    }

    private numberFormatOptions(nbr: number, currency?: Enums.Currency) {
        if (currency) {
            return {
                style: "currency",
                currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            };
        }
        return nbr % 1 !== 0 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {};
    }
}

angular.module("thor.core.locale-number.service", []).service("localeNumber", LocaleNumber);
